/*
 * (c) copyright 2012-2024 mgm technology partners GmbH.
 * This software, the underlying source code and other artifacts are protected by copyright.
 * All rights, in particular the right to use, reproduce, publish and edit are reserved.
 * A simple right of use (license) can be acquired for use, duplication, publication, editing etc..
 * Requests for this can be made at A12-license@mgm-tp.com or other official channels of the copyright holder.
 */

import { call, put, SagaGenerator, select } from "typed-redux-saga";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { ModelSagas } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import {
	FormActivity,
	platformSingleDocumentDataProvider
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { isFormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DocumentModel, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { DataProvider } from "@com.mgmtp.a12.client/client-core/lib/core/data";

import { assertNonNullable } from "../utilities/assert";
import { addDocument, modifyDocument } from "../utilities/rpcHelper";

const documentService = new DocumentServiceFactory().getDocumentService();

/**
 * //TODO: MODULF-3845
 * This overwrites the default platformSingleDocumentDataProvider.
 * With A12 2024.6 fields which are configured with notRelevant (dependent field configuration) are not shown in the UI
 * and they are also not persisted. The fields will be also completely removed from the document.
 * This is new behavior and we need to adjust our models to this behavior. As a workaround for now we just skipping the
 * filtering of "not relevant" fields by overwriting the save operation of the platformSingleDocumentDataProvider
 *
 *
 * @important
 * @warning
 */
export const modulfPlatformSingleDocumentDataProvider: DataProvider = {
	name: "ModulfPlatformSingleDocumentDataProvider",
	canHandle: platformSingleDocumentDataProvider.canHandle,
	*provideData(config: DataProvider.ProvideDataConfig): SagaGenerator<void> {
		if (config.operation === "save") {
			yield* call(handleSave, config);
		} else {
			yield* call(platformSingleDocumentDataProvider.provideData, config);
		}
	}
};

function* handleSave(config: DataProvider.SaveConfig): SagaGenerator<void> {
	const { activityId, dataHolders } = config;

	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (activity === undefined) {
		throw new Error(`No activity found for id ${activityId}.`);
	}
	assertNonNullable(activity?.descriptor.instance);

	const defaultDataHolder = dataHolders.find(Activity.DataHolder.hasDescriptor(activity.descriptor));
	if (defaultDataHolder === undefined) {
		throw new Error(`No default DataHolder found for activityId ${activityId}`);
	}

	const { updateActivityData, saving } = config.details;

	const { data: oldData, datasourceActivityId } = defaultDataHolder;
	if (oldData === undefined) {
		throw new Error("Cannot handle empty data");
	}

	if (!FormActivity.Data.SingleDocumentData.isInstance(oldData)) {
		throw new Error("Activity does not contain suitable data!");
	}

	const documentModel = yield* call(waitForModels, activity);

	let data: FormActivity.Data.SingleDocumentData | undefined = oldData;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { modelId, id, ...documentWithoutModelAndDocumentId } = oldData.document;

	const docForServer = documentService.formatDates(documentWithoutModelAndDocumentId, documentModel);

	if (activity.descriptor.instance === NEW_INSTANCE_IDENTIFIER) {
		const result = yield* call(addDocument, modelId, docForServer);
		data = { document: { ...oldData.document, id: result.docRef } };
	} else {
		yield* call(modifyDocument, oldData.document.id, docForServer);
	}

	if (data !== undefined) {
		if (datasourceActivityId !== undefined) {
			yield* put(
				ActivityActions.setData({
					activityId: datasourceActivityId,
					data
				})
			);
		}

		if (updateActivityData) {
			yield* put(ActivityActions.setData({ activityId, data }));
		}
	}

	yield* put(
		saving.done({
			instance:
				"document" in data && Activity.Data.Document.isInstance(data.document) ? data.document.id : undefined
		})
	);

	// we need to store the relatedActivityId before, we do the activity's commit
	const relatedActivityId = datasourceActivityId || activity.initiatingActivityId;
	if (relatedActivityId && !activity.descriptor.modelId?.endsWith("_Prozess_WF")) {
		yield* put(ActivityActions.reloadData({ activityId: relatedActivityId }));
	}
}

function* waitForModels(activity: Activity): SagaGenerator<DocumentModel> {
	const models = yield* call(() => ModelSagas.waitForModelsLoaded(activity.id));

	const fm = models.find(isFormModel);
	assertNonNullable(fm);

	const modelReference = fm.header.modelReferences?.find(({ modelType }) => modelType === "document");

	const dm = models.find((m): m is DocumentModel => m.header.id === modelReference?.reference);

	assertNonNullable(dm);

	return dm;
}
